import { JSX, useEffect, useRef, useState } from 'react';
import { CalendarEvent, calendarService } from '../../services/calendarService';
import './eventView.css';
import rainbow from '../../assets/images/icons/rainbow.png';
import taiwan from '../../assets/images/icons/taiwan.png';
import usa from '../../assets/images/icons/burger.png';
import tobacco from '../../assets/images/icons/tobacco.png';
import clock from '../../assets/images/icons/clock.png';
import hyte from '../../assets/images/icons/packaging.png';
import { createPortal } from 'react-dom';
import DatePicker from 'react-datepicker';

const EventView = ({event, displayDate, handelEventUpdate}: {event?: CalendarEvent, displayDate: boolean, handelEventUpdate: () => void}): JSX.Element => {
    const [startTime, setStartTime] = useState<Date>(new Date());
    const [endTime, setEndTime] = useState<Date>(new Date());
    const [isFullDay, setIsFullDay] = useState<boolean>(false);
    const [isEventOver, setIsEventOver] = useState<boolean>(false);
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const triggerRef = useRef(null);
    
    useEffect(() => {
        getDateRange(event);
    }, [event]);

    useEffect(() => {
        setIsEventOver(new Date() > endTime);
    }, [endTime]);

    const getDateRange = (event: any) => {
        if (!event || !event.start || !event.end) {
            console.log("Missing event or event.start", event);
            return new Date();
        }
        
        const dateString = event.start.dateTime || event.start.date;
        const endDateString = event.end.dateTime || event.end.date;
        const isFullDay = event.start.dateTime === event.end.dateTime;
        setIsFullDay(isFullDay);
        setStartTime(new Date(dateString));
        setEndTime(new Date(endDateString));
    }
    
    const getDate = (dateString: any): Date => {
        if (!dateString) {
            console.log("No date string found in event", event);
            return new Date();
        }
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                console.log("Invalid date string:", dateString);
                return new Date();
            }
            
            const dateOnly = new Date(
                date.getFullYear(), 
                date.getMonth(), 
                date.getDate()
            );
            
            return dateOnly;
        } catch (error) {
            console.error("Error parsing date:", error);
            return new Date();
        }
    };

    const getCalendarIcon = (event: CalendarEvent): string => {
        if (event.calendarId === 'primary') { return rainbow; }
        if (event.calendarId === 'taiwan holiday') { return taiwan; }
        if (event.calendarId === 'us holiday') { return usa; }
        if (event.calendarId === 'work') { return tobacco; }
        if (event.calendarId === 'HYTE') { return hyte; }
        return '../../assets/icons/rainbow.png';
    }

    const getDisplayDayTime = (startTime: Date, endTime: Date): string => {
        const startHour = startTime.getHours() % 12 || 12;
        const endHour = endTime.getHours() % 12 || 12;
        const startMin = startTime.getMinutes().toString().padStart(2, '0');
        const endMin = endTime.getMinutes().toString().padStart(2, '0');
        const startAmPm = startTime.getHours() >= 12 ? 'PM' : 'AM';
        const endAmPm = endTime.getHours() >= 12 ? 'PM' : 'AM';

        if (startAmPm === endAmPm) {
            return `${startHour}${startMin == '00' ? '' : ':' + startMin} - ${endHour}${endMin == '00' ? '' : ':' + endMin} ${startAmPm}`;
        }
        return `${startHour}${startMin == '00' ? '' : ':' + startMin} ${startAmPm} - ${endHour}${endMin == '00' ? '' : ':' + endMin} ${endAmPm}`;
    }

    const getEventSummaryClassName = (calendarId: string): string => {
        if (isEventOver) { return ''}
        if (calendarId === 'taiwan holiday') { return 'event-view-summary-taiwan-holiday'; }
        else if (calendarId === 'us holiday') { return 'event-view-summary-us-holiday'; }
        else if (calendarId === 'work') { return 'event-view-summary-work'; }
        else if (calendarId === 'primary') { return 'event-view-summary-personal'; }
        return 'event-view-summary';
    }

    const handleInputClick = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX
        });
        console.log(position.top, position.left);
        setIsOpen(!isOpen);
    };

    const handleDateChange = (newDate : Date) => {
        console.log("newDate", newDate);
        changeEventDate(newDate);
        setIsOpen(false);
        handelEventUpdate();
    };

    const changeEventDate = (newDate : Date) => {
        event.start.date = newDate.toISOString().split('T')[0];
        event.end.date = newDate.toISOString().split('T')[0];
        calendarService.updateEvent(event);
    }

    return (
        <div>
            <div className='event-view'>
                {displayDate ? (
                    <div className='event-view-date-container'>
                        <div 
                        className='event-view-clickable-date'
                        ref={triggerRef}
                        onClick={handleInputClick} >
                            <p className={startTime.toLocaleDateString('default', { month: 'long', day: 'numeric' }) === new Date().toLocaleDateString('default', { month: 'long', day: 'numeric' }) ? 'event-view-date-today' : 'event-view-date'}>{startTime.getDate()}</p>
                            <p className='event-view-month'>{startTime.toLocaleString('default', { month: 'short' }) + ' ' + startTime.toLocaleString('default', {weekday: 'short'})}</p>
                        </div>
                        {isOpen && createPortal(
                        <div id="custom-date-portal">
                            <div className="date-picker-overlay" onClick={() => setIsOpen(false)} />
                            <div 
                                className="date-picker-calendar" 
                                style={{ 
                                position: 'absolute',
                                top: `${position.top}px`,
                                left: `${position.left}px` 
                                }}
                            >
                                <DatePicker
                                selected={startTime}
                                onChange={(date: Date) => handleDateChange(date)}
                                showMonthDropdown
                                showYearDropdown
                                yearItemNumber={100}
                                scrollableYearDropdown={true}
                                dropdownMode="scroll"
                                inline  // Important: render as inline calendar
                                />
                         </div>
                        </div>,
                        document.body
                        )}
                    </div>
                ) : (<div className='event-view-date-container'/>)}
                <div className={isEventOver ? 'event-display-container-over' : 'event-display-container'}>
                    <div className='event-view-daytime'>
                        {event && <img src={clock} alt='event-icon' className='event-view-icon'/>}
                        {event? (
                            <p>{isFullDay ? 'Full Day' : getDisplayDayTime(startTime, endTime)}</p>) :
                            <></>}
                    </div>
                    {event? (
                        <div className='event-discription-container'>
                            <img src={getCalendarIcon(event)} alt='event-icon' className='event-view-icon'/>
                            <p className={getEventSummaryClassName(event.calendarId) + ' event-view-summary'}>{event.summary}</p>
                        </div>
                    ) : (
                        <p className='event-view-summary'>'Nothing To Doooooooo Today 😌'</p>
                    )}
                </div>
            </div>
        </div>
    )
}

export default EventView;